import React from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo_basic.png";
import { useAuth } from "../context/AuthProvider";
import AppFooter from "../components/AppFooter";


export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { session } = useAuth();

  return (
    <div className="min-h-screen bg-[#11111f] text-white font-sans antialiased">
      {/* =====================================================
          MAIN WEBSITE HEADER
      ===================================================== */}
      <header className="sticky top-0 z-50 border-b border-slate-800 bg-[#11111f]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-4">
            {/* LANDING-STYLE LOGO / BRAND */}
            <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => navigate("/landing")}
                className="flex items-center gap-3 cursor-pointer min-w-0 text-left"
                aria-label="Go to Reignite Spirit home page"
              >
                <img
                  src={logo}
                  alt="Reignite Spirit Logo"
                  className="w-10 sm:w-12 h-10 sm:h-12 drop-shadow-[0_0_14px_#eb6fbd] flex-shrink-0"
                  width="48"
                  height="48"
                  loading="eager"
                  decoding="async"
                />

                <div className="min-w-[190px]">
                  <h1 className="text-base sm:text-lg font-semibold tracking-wide text-[#EB6AB5] whitespace-nowrap">
                    REIGNITE.SPIRIT
                  </h1>

                  <p className="text-[10px] sm:text-xs text-[#EB6AB5]/80 tracking-[0.15em] uppercase whitespace-nowrap">
                    Rekindle Purpose
                  </p>
                </div>
              </button>
            </div>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-5 text-sm font-sans">
              <Link
                to="/landing"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Home
              </Link>

              <Link
                to="/snapshot"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Snapshot
              </Link>

              <Link
                to="/deep-audit"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Deep Audit
              </Link>

              <Link
                to="/blog"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Career Intelligence
              </Link>

              <Link
                to="/faq"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                FAQ
              </Link>

              {session ? (
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                  >
                    Login
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/register")}
                    className="px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition whitespace-nowrap"
                  >
                    Register
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/choose-plan")}
                    className="px-3 py-2 rounded-lg bg-[#cb01bd] hover:bg-[#e94ce0] text-white font-bold transition whitespace-nowrap"
                  >
                    Get My Audit
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* =====================================================
          PRIVACY CONTENT
      ===================================================== */}
      <main className="px-6 py-10">
        <div className="max-w-4xl mx-auto bg-[#141428] border border-[#EB6AB5]/20 rounded-2xl p-6 sm:p-10 shadow-lg space-y-8">
          <h1 className="text-3xl font-bold text-[#EB6AB5] text-center">
            PRIVACY POLICY
          </h1>

          <p className="text-gray-300 text-sm text-center">
            <strong className="text-[#EB6AB5]">Effective Date:</strong>{" "}
            December 12, 2025
          </p>

          <p className="text-gray-300 text-sm leading-relaxed">
            Reignite.Spirit Ltd ("we," "our," or "us") is committed to
            protecting your privacy. This Privacy Policy explains how we
            collect, use, disclose, and safeguard your information when you use
            our AI-powered career strategy platform (the "Service").
          </p>

          <p className="text-gray-300 text-sm leading-relaxed">
            By accessing or using the Service, you consent to the data practices
            described in this policy.
          </p>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              1. INFORMATION WE COLLECT
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              We collect information that allows us to generate your career
              roadmap and facilitate your transaction.
            </p>

            <h3 className="text-lg font-semibold text-[#EB6AB5] mt-4">
              1.1. Personal Data You Provide:
            </h3>

            <ul className="list-disc ml-6 mt-2 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Identity Data:</strong> Name, age, and email address.
              </li>

              <li>
                <strong>Career Data:</strong> Current role, industry, salary
                expectations, financial runway, and educational background.
              </li>

              <li>
                <strong>Psychometric Data:</strong> Your O*NET interest codes
                and work environment preferences.
              </li>

              <li>
                <strong>Skill Data:</strong> Technical and soft skills you
                manually input.
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-[#EB6AB5] mt-6">
              1.2. Payment Data (Important):
            </h3>

            <ul className="list-disc ml-6 mt-2 text-gray-300 text-sm space-y-2">
              <li>
                We <strong>DO NOT</strong> collect or store your full credit
                card number, expiration date, or CVV.
              </li>

              <li>
                All payment data is handled directly by our third-party payment
                processor (e.g., Stripe). We only receive a transaction token
                (a receipt ID) to confirm payment success.
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-[#EB6AB5] mt-6">
              1.3. Automated Data:
            </h3>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              When you visit our site, we may automatically collect standard
              technical data such as your IP address, browser type, and
              operating system to ensure the website functions correctly.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              2. HOW WE USE YOUR INFORMATION
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              We use your data for the following specific purposes:
            </p>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>To Generate Your Report:</strong> Your career data is
                processed by our algorithms and Third-Party AI Models
                (specifically Google Vertex AI) to create your PDF Career
                Roadmap.
              </li>

              <li>
                <strong>To Process Payments:</strong> To verify with our payment
                gateway that your transaction was successful.
              </li>

              <li>
                <strong>To Communicate:</strong> To send your report to your
                email address and provide customer support.
              </li>

              <li>
                <strong>To Improve Our Models:</strong> Anonymized data
                (stripped of your name and email) may be used to refine our
                prompt engineering and accuracy.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              3. SHARING OF YOUR INFORMATION
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              We do not sell your personal data. We only share data with the
              trusted third-party service providers required to operate the
              Service:
            </p>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>AI Service Providers:</strong> We transmit your career
                and skill data to Google Cloud Platform (Vertex AI) for the sole
                purpose of generating the analysis. Google is contractually
                obligated to protect this data.
              </li>

              <li>
                <strong>Payment Processors:</strong> We share transaction
                identifiers with our Payment Gateway to validate purchases.
              </li>

              <li>
                <strong>Cloud Infrastructure:</strong> Our database and website
                are hosted on secure cloud servers (e.g., Supabase, Google
                Cloud).
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              4. DATA SECURITY
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              We use administrative, technical, and physical security measures
              to help protect your personal information.
            </p>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Encryption:</strong> Data is encrypted in transit
                (SSL/TLS) and at rest where applicable.
              </li>

              <li>
                <strong>Access Control:</strong> Access to your personal data is
                restricted to authorized personnel of Reignite.Spirit Ltd.
              </li>

              <li>
                <strong>Disclaimer:</strong> While we implement robust security
                measures, no transmission over the Internet is 100% secure. You
                provide your information at your own risk.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              5. COOKIE POLICY
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              This section explains how Reignite.Spirit Ltd uses cookies and
              similar technologies to recognize you when you visit our website.
            </p>

            <h3 className="text-lg font-semibold text-[#EB6AB5] mt-4">
              5.1. What are Cookies?
            </h3>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              Cookies are small data files that are placed on your computer or
              mobile device when you visit a website. They are widely used by
              website owners to make their websites work, or to work more
              efficiently, as well as to provide reporting information.
            </p>

            <h3 className="text-lg font-semibold text-[#EB6AB5] mt-4">
              5.2. How We Use Cookies
            </h3>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              We use two specific types of cookies on our platform:
            </p>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Essential Cookies (Strictly Necessary):</strong> These
                are absolutely necessary for the website to function. We use
                them to:

                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li>
                    Remember your "Session State" (e.g., whether you selected
                    the 'Basic' or 'Premium' plan) as you move from the Landing
                    Page to the Questionnaire.
                  </li>

                  <li>
                    Ensure secure payments via Stripe (or your chosen gateway).
                    These tools place cookies to detect fraud and ensure your
                    transaction is secure. You cannot opt out of these, as the
                    site will not function without them.
                  </li>
                </ul>
              </li>

              <li>
                <strong>Analytics & Performance Cookies:</strong> These help us
                understand how visitors interact with our website (e.g., which
                pages are visited most often).

                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li>
                    <strong>Google Analytics:</strong> We may use Google
                    Analytics to collect anonymous data on site usage. These
                    cookies are non-essential, and we will only set them if you
                    click "Accept" on our Cookie Banner.
                  </li>
                </ul>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-[#EB6AB5] mt-6">
              5.3. Managing Your Preferences
            </h3>

            <ul className="list-disc ml-6 mt-2 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Consent Banner:</strong> Upon your first visit, a banner
                will appear allowing you to "Accept" or "Reject" non-essential
                cookies.
              </li>

              <li>
                <strong>Browser Controls:</strong> You can set or amend your web
                browser controls to accept or refuse cookies. If you choose to
                reject strictly necessary cookies, you may still use our
                website, but access to some functionality (like payments) may
                be restricted.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              6. INTERNATIONAL DATA TRANSFERS
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              Reignite.Spirit Ltd is based in the Abu Dhabi Global Market
              (ADGM). However, our technical infrastructure (Google Cloud) is
              global. By using the Service, you acknowledge and consent that
              your data may be transferred to, and processed in, servers
              located outside the UAE (such as the United States) where data
              protection laws may differ.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              7. DATA RETENTION
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              We retain your personal data only for as long as necessary to
              fulfill the purposes outlined in this policy.
            </p>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Transaction Records:</strong> Retained for accounting
                and legal compliance.
              </li>

              <li>
                <strong>Career Data:</strong> Retained to allow for the 7-day
                "Right to Cure" review period. You may request deletion of your
                profile data after this period.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              8. YOUR RIGHTS
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              Depending on your location (and under ADGM Data Protection
              Regulations), you may have the following rights:
            </p>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Right to Access:</strong> Request a copy of the personal
                data we hold about you.
              </li>

              <li>
                <strong>Right to Correction:</strong> Request that we fix
                incorrect data (e.g., a wrong email address).
              </li>

              <li>
                <strong>Right to Deletion:</strong> Request that we delete your
                personal data from our active databases.
              </li>
            </ul>

            <p className="text-gray-300 text-sm mt-4 leading-relaxed">
              To exercise these rights, please contact us at:{" "}
              <strong className="text-[#EB6AB5]">
                support@reignitespirit.com
              </strong>
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              9. CHANGES TO THIS POLICY
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              We may update this Privacy Policy from time to time. The updated
              version will be indicated by an updated "Effective Date" at the
              top of this page. We encourage you to review this privacy policy
              frequently to be informed of how we are protecting your
              information.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              10. CONTACT US
            </h2>

            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us
              at:
            </p>

            <p className="text-gray-300 text-sm mt-3 leading-relaxed">
              <strong className="text-[#EB6AB5]">
                Reignite.Spirit Ltd
              </strong>
              <br />
              Abu Dhabi Global Market (ADGM)
              <br />
              Abu Dhabi, UAE
              <br />
              Email:{" "}
              <strong className="text-[#EB6AB5]">
                support@reignitespirit.com
              </strong>
            </p>
          </div>
        </div>
      </main>
      <AppFooter
  variant="landing"
  logoSrc={logo}
/>
    </div>
  );
}